import {Injectable, signal} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Params} from '@angular/router';
import {CommonTableData} from '@lib-ui/common-table/models/common-table-data.class';
import {MessageService} from '@lib-services/message.service';
import {ICommonTableConfig, ICommonTableService} from '@lib-ui/common-table/models/common-table.interface';
import {IPaginatedResponse} from '@lib-typing/response.interface';
import {LogsService} from '@lib-services/logs.service';
import {ILog, ILogTableData} from '@lib-typing/logs.interface';
import {PartSearchService} from '@lib-services/part-search.service';
import { TimeZoneService } from '@lib-services/time-zone.service';
import { EnumTranslateService } from '@lib-services/enum-translate.service';

@Injectable({
    providedIn: 'root',
})
export class LogsTableService implements ICommonTableService<ILogTableData> {
    public tableData$: BehaviorSubject<CommonTableData<ILogTableData>> = new BehaviorSubject<CommonTableData<ILogTableData>>(new CommonTableData());
    defaultFilterParams = {
        sortBy: 'createdAt:DESC',
    };
    private readonly commonTableData = new CommonTableData();
    loading = signal<boolean>(false);

    constructor(
        private logsService: LogsService,
        private messageService: MessageService,
        private partSearchService: PartSearchService,
        private timeZoneService: TimeZoneService,
        private enumTranslateService: EnumTranslateService,
    ) {}

    updateData(config: ICommonTableConfig): void {
        this.loading.set(true);
        this.logsService
            .getLogs(this.createSearchParams(config))
            .subscribe({
                next: response => this.tableData$.next(new CommonTableData(this.transformData(response))),
                error: () => this.messageService.errorMessage('Ошибка загрузки списка'),
            })
            .add(() => this.loading.set(false));
    }

    createSearchParams(config: ICommonTableConfig): Params {
        this.commonTableData.data.length = 0;
        const { pageIndex, itemsPerPage } = config.paginatorConfig;
        const { filters } = config;

        let searchParams: Params = {
            ...this.defaultFilterParams,
            limit: itemsPerPage.toString(),
        };

        if (pageIndex) {
            searchParams = {
                ...searchParams,
                page: pageIndex.toString(),
            };
        }

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    let filterValue;
                    let params: string[];
                    switch (key) {
                    case 'user.firstName':
                        params = String(value).split(' ').map((el: string) => el.trim());
                        filterValue = [`$ilike:${params[0]}`].concat(this.partSearchService.createMultiStringParams(params.slice(1)));
                        break;
                    case 'createdAt':
                        filterValue = `$btw:${value}`;
                        break;
                    default:
                        filterValue = `${value}`;
                    }
                    searchParams = {
                        ...searchParams,
                        [`filter.${key}`]: filterValue,
                    };
                }
            });
        }

        return searchParams;
    }

    transformData(response: IPaginatedResponse<ILog>): IPaginatedResponse<ILogTableData> {
        const transformedData = response.data.map(log => {
            return {
                createdAt:this.timeZoneService.setCurrentTimeZoneDate(log.createdAt),
                user: (log.user?.firstName || '') + ' ' + (log.user?.lastName || ''),
                userId: log.user?.id,
                role: this.enumTranslateService.translateRole(log.user?.role),
                action: this.enumTranslateService.translateAction(log.action),
                entityId: log.entityId,
                entityName: this.enumTranslateService.translateEntitiesName(log.entityName),
                body: log.body,
            } as ILogTableData;

        });
        // Return a new response object with transformed data
        return {
            ...response,
            data: transformedData
        };
    }
}
